// @ts-nocheck
import { useEffect, useMemo, useRef } from 'react';
import { AlertCircle, Check, ChevronRight, Pause } from 'lucide-react';
import { rollUp, visibleRows } from './treeState';
import { c } from './ui/ui';


const TONE = { done: 'ok', attention: 'bad', paused: 'off', empty: '' };

// red "!" only on a required row that is not done yet (never on parent groups)
const needsBang = (row, statuses) => row.need === 'required' && statuses[row.id] !== 'done';


function rowNote(row, status) {
  if (row.badge) return row.badge;
  if (status === 'attention') return 'Fix';
  if (status === 'paused') return 'Off';
  if (row.need === 'optional' && row.status !== 'done') return 'Optional';
  if (status === 'done') return 'Done';
  return '';
}

export default function BuilderTree({ nodes, selectedId, onSelect, expanded, onToggle, search, className }) {
  const statuses = useMemo(() => rollUp(nodes), [nodes]);
  const rows = useMemo(() => visibleRows(nodes, expanded), [nodes, expanded]);
  const listRef = useRef(null);

  // keep the selected row in view when the palette or a checklist click jumps
  useEffect(() => {
    listRef.current?.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const onKeyDown = (e) => {
    const i = rows.findIndex((r) => r.id === selectedId);
    const row = rows[i];
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      onSelect(rows[Math.min(i + 1, rows.length - 1)]?.id ?? rows[0]?.id);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onSelect(rows[Math.max(i - 1, 0)]?.id ?? rows[0]?.id);
    } else if (e.key === 'ArrowRight' && row?.children?.length && !expanded[row.id]) {
      e.preventDefault();
      onToggle(row.id);
    } else if (e.key === 'ArrowLeft' && row?.children?.length && expanded[row.id]) {
      e.preventDefault();
      onToggle(row.id);
    }
  };

  const renderRow = (row, depth) => {
    const isParent = !!row.children?.length;
    const selected = row.id === selectedId;
    const status = statuses[row.id];
    const tone = TONE[status] ?? '';
    const note = rowNote(row, status);
    return (
      <div key={row.id}>
        <button
          type="button"
          role="treeitem"
          aria-selected={selected}
          aria-expanded={isParent ? !!expanded[row.id] : undefined}
          aria-current={selected ? 'true' : undefined}
          data-selected={selected}
          tabIndex={-1}
          onClick={() => {
            onSelect(row.id);
            if (isParent) onToggle(row.id);
          }}
          className={c('ni', tone, selected && 'cur', depth > 0 && 'sub')}
        >
          <span className={c('d')}>
            {tone === 'ok' ? <Check className="ags-i" /> : tone === 'bad' ? <AlertCircle className="ags-i" /> : tone === 'off' ? <Pause className="ags-i" /> : null}
          </span>
          <span className={c('nl')}>{row.label}</span>
          {row.need === 'required' && (
            <span aria-hidden className={c('req')} title="Required">
              *
            </span>
          )}
          {needsBang(row, statuses) && !selected && <span className="ags-sr">Required, not done yet</span>}
          <small>{note}</small>
          {isParent && <ChevronRight className={c('chev', expanded[row.id] && 'open')} />}
        </button>
        {isParent && expanded[row.id] && row.children.map((ch) => renderRow({ ...ch }, depth + 1))}
      </div>
    );
  };

  return (
    <nav
      ref={listRef}
      aria-label="Agent sections"
      role="tree"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={c('nav', className)}
    >
      {search}
      <div className={c('ngw')}>
        {nodes.map((node) => {
          const own = !!node.need || node.status !== undefined || node.id === 'laya';
          return own ? (
            <div className={c('ng')} key={node.id}>
              {renderRow(node, 0)}
            </div>
          ) : (
            <div className={c('ng')} key={node.id}>
              <h4>{node.label}</h4>
              {node.children.map((ch) => renderRow(ch, 0))}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
